import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

// reusable components
import { TableRow, TableCell } from "@/components/ui/table";

// utils and helpers
import { searchFilterByFields } from "../utils/searchFilterExpenses";
import { formatCurrency } from "@/utils/formatCurrency";
import { fetchSuppliers } from "@/services/financeApi";
import { useBusiness } from "@hooks/useBusiness";

export default function useSuppliersTable(searchTerm = "") {
  const { businessId } = useBusiness();

  const {
    data: suppliers = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    // Derived from each product's prodSupplier block - there is no suppliers
    // collection in Firestore.
    queryKey: ["finance-suppliers", businessId],
    queryFn: () => fetchSuppliers(businessId),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  const filteredSuppliers = useMemo(
    () => searchFilterByFields(suppliers, searchTerm, ["name", "contactEmail", "taxNumber"]),
    [suppliers, searchTerm]
  );

  const supplierColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Supplier",
        cell: ({ row }) => (
          <span className="font-medium text-[#1a1a1a]">{row.original.name}</span>
        ),
      },
      {
        accessorKey: "taxNumber",
        header: "Tax Number",
        cell: ({ row }) => row.original.taxNumber || "-",
      },
      {
        accessorKey: "contactEmail",
        header: "Email",
        cell: ({ row }) => row.original.contactEmail || "-",
      },
      {
        accessorKey: "contactPhone",
        header: "Phone",
        cell: ({ row }) => row.original.contactPhone || "-",
      },
      {
        accessorKey: "totalPurchases",
        header: "Total Purchases",
        cell: ({ row }) => formatCurrency(row.original.totalPurchases),
      },
      {
        accessorKey: "openBalance",
        header: "Open Balance",
        cell: ({ row }) => {
          const balance = row.original.openBalance ?? 0;
          return (
            <span className={balance > 0 ? "font-medium text-red-600" : "text-[#555]"}>
              {formatCurrency(balance)}
            </span>
          );
        },
      },
    ],
    []
  );

  const renderSupplierRow = useCallback((row) => {
    return (
      <TableRow key={row.id} className="hover:bg-[#f6f6ff] transition-colors">
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} className="px-4 py-3 text-[13px] font-medium">
            {cell.getContext().column.columnDef.cell?.(cell.getContext()) ?? cell.renderCell()}
          </TableCell>
        ))}
      </TableRow>
    );
  }, []);

  return {
    allSuppliers: suppliers,
    suppliers: filteredSuppliers,
    supplierColumns,
    renderSupplierRow,
    refetch,
    isLoading,
    isError,
  };
}
